// tslint:disable:react-hooks-nesting
import { useMutation, useQuery } from '@apollo/client';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  Divider,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { red } from '@mui/material/colors';
import * as Schema from 'generated/graphql/schema';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import { MutationIconButton } from '@/components/buttons/mutation-button';
import { deviceAttachCamera, deviceDetachCamera } from '@/graphql/mutations';
import { devicesSimple } from '@/graphql/queries';
import * as Types from '@/types';
import { SensorSettingsClasses } from '@/views/dialogs/sensor-settings/sensor-settings-dialog';

import { TabPropsType } from '.';

const isShown = (peripheralType: Schema.PeripheralType) => {
  return peripheralType === Schema.PeripheralType.CAMERA;
};

export const TabProps: TabPropsType = {
  isShown: (s) => isShown(s.peripheralType),
  label: (t) => t('device:cameraSensors', { defaultValue: 'Attached sensors' }),
};

const useStyles = makeStyles()(() => ({
  cameraAttachementContainer: {
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '20px',
    paddingRight: '20px',
    marginTop: '10px',
  },
  cameraAttachementHeader: {
    fontWeight: 500,
    marginBottom: '5px',
  },
  iconButton: {
    padding: '12px',
  },
}));

interface Properties {
  peripheral: Partial<Schema.Camera>;
  invalidFormMessage: React.ReactNode | null;
  classes: SensorSettingsClasses;
}

export const TabPanel: React.FunctionComponent<Properties> = ({ invalidFormMessage, classes, ...props }) => {
  const { classes: internalClasses } = useStyles();
  const { t } = useTranslation(['device']);

  const [peripheral, setPeripheral] = useState(props.peripheral);
  const [attachSensorMenuAnchor, setAttachSensorMenuAnchor] = useState<null | HTMLElement>(null);
  const [detachState, setDetachState] = useState<null | string>(null);

  const { attachedSensors, peripheralId } = peripheral;
  const actualAttachedSensors = (attachedSensors || []).filter(Types.isTruthy);

  const { data } = useQuery<Schema.DevicesSimpleQuery, Schema.DevicesSimpleQueryVariables>(devicesSimple);

  const [attachCamera, { loading: attachLoading }] = useMutation<
    Schema.DeviceAttachCameraMutation,
    Schema.DeviceAttachCameraMutationVariables
  >(deviceAttachCamera);
  const [detachCamera, { loading: detachLoading }] = useMutation<
    Schema.DeviceDetachCameraMutation,
    Schema.DeviceDetachCameraMutationVariables
  >(deviceDetachCamera);
  if (peripheral.peripheralType && !isShown(peripheral.peripheralType)) {
    return null;
  }

  const attachCam = (sensor: Schema.Sensor) => () => {
    if (!peripheralId) {
      return;
    }

    attachCamera({
      variables: { peripheralId, sensorPeripheralId: sensor.peripheralId },
    });
    setAttachSensorMenuAnchor(null);
    const newAttachedSensorMap = actualAttachedSensors.concat([
      {
        _id: null,
        peripheralId: sensor.peripheralId,
        name: sensor.name,
        peripheralType: sensor.peripheralType,
        description: sensor.description,
      } as Schema.Sensor,
    ]);
    setPeripheral({ ...peripheral, attachedSensors: newAttachedSensorMap });
  };

  const detachCam = (sensor: Schema.Sensor) => () => {
    if (!peripheralId) {
      return;
    }

    setDetachState(sensor.peripheralId);
    detachCamera({
      variables: { peripheralId, sensorPeripheralId: sensor.peripheralId },
    });
    const newAttachedSensorMap = actualAttachedSensors.filter((row) => row.peripheralId !== sensor.peripheralId);
    setPeripheral({ ...peripheral, attachedSensors: newAttachedSensorMap });
  };
  const handleMenuClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => setAttachSensorMenuAnchor(e.currentTarget);
  const handleMenuClose = () => setAttachSensorMenuAnchor(null);
  const sensors = (data?.devices ?? []).reduce<Schema.Sensor[]>(
    (acc, cur) =>
      acc.concat(
        (cur?.peripherals ?? [])
          .filter(Types.isSensor)
          .filter((p) => !actualAttachedSensors.some(({ peripheralId }) => peripheralId === p.peripheralId)),
      ),
    [],
  );
  let sorted = false;
  if (!sorted) {
    sensors.sort((l, r) => l.name.localeCompare(r.name));
    sorted = true;
  }
  const menuItems = sensors.length ? (
    sensors.map((sensor) => (
      <MenuItem key={sensor.peripheralId} onClick={attachCam(sensor)}>
        {sensor.name}
      </MenuItem>
    ))
  ) : (
    <MenuItem>
      {t(['device:appearsNoMoreSensors'], {
        defaultValue: 'It appears you dont have access to any more sensors',
      })}
    </MenuItem>
  );
  return (
    <div className={classes.tabPanel}>
      <div className={internalClasses.cameraAttachementContainer}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" className={internalClasses.cameraAttachementHeader}>
            {t(['device:sensorsAttached'], { defaultValue: 'Sensors attached to this camera' })}
          </Typography>
          <MutationIconButton
            aria-label={t(['shared:add'], { defaultValue: 'Add' })}
            loading={attachLoading}
            className={internalClasses.iconButton}
            onClick={handleMenuClick}
            tooltipMsg={t(['device:attachCameraToSensor'], { defaultValue: 'Attach camera to sensor' })}
          >
            <AddIcon color="primary" />
          </MutationIconButton>
        </div>
        <List dense>
          {actualAttachedSensors.map((sensor, index, arr) => (
            <React.Fragment key={sensor.peripheralId}>
              <ListItem>
                <ListItemText primary={sensor.name} secondary={sensor.description} />
                <ListItemSecondaryAction>
                  <MutationIconButton
                    aria-label={t(['shared:remove'], { defaultValue: 'Remove' })}
                    loading={detachLoading && detachState === sensor.peripheralId}
                    className={internalClasses.iconButton}
                    onClick={detachCam(sensor)}
                    loadingColor={red[500]}
                    tooltipMsg={t(['device:dettachCameraToSensor'], { defaultValue: 'Detach camera from sensor' })}
                  >
                    <DeleteIcon />
                  </MutationIconButton>
                </ListItemSecondaryAction>
              </ListItem>
              {index < arr.length - 1 ? <Divider /> : null}
            </React.Fragment>
          ))}
        </List>
        <Menu
          id="simple-menu"
          anchorEl={attachSensorMenuAnchor}
          open={Boolean(attachSensorMenuAnchor)}
          onClose={handleMenuClose}
          PaperProps={{
            style: {
              maxHeight: '300px',
            },
          }}
        >
          {menuItems}
        </Menu>
      </div>
      {invalidFormMessage}
    </div>
  );
};
